import {
  DefaultListItem,
  DefaultListItemNonSelectable,
} from "../../../-components/default-list/list-item"
import { useInViewport, useDisclosure } from "@mantine/hooks"
import { useGetAuthenticatedUser } from "../../app/-components/use-authenticated-user"
import { ChatMessagePaper } from "./ChatMessagePaper"
import { PersonProfilePictureAvatar } from "../../app/-components/person-profile-picture-avatar"
import { ActionIcon, CopyButton } from "@mantine/core"
import { IconCheck, IconCopy, IconMessageReply } from "@tabler/icons-react"
import { useTranslation } from "react-i18next"
import useClipboardAvailable from "../../../typed-client/hooks/useClipboardAvailable"
import { ChatPostDto } from "@/orval/gen/model"
import { ActionIconPencil } from "@components/action-icons"
import { UpdateMessageModal } from "./UpdateMessageModal"
import dayjs from "dayjs"
import { useBatchedSimpleUser } from "@/-dataloaders/simple-users"

interface Props extends DefaultListItemNonSelectable {
  message: ChatPostDto
  onInViewportChange?: (inView: boolean) => void
  onReplyButtonClick?: () => void
  showAvatars?: boolean
  showUserName?: boolean
  readOnly: boolean
  showReadIndicator: boolean
  highlight?: string
  isFocused?: boolean
}

export function MessageListItem({
  message,
  showAvatars,
  showUserName,
  onInViewportChange,
  onReplyButtonClick,
  readOnly,
  showReadIndicator,
  isFocused,
  highlight,
  ...props
}: Props) {
  const { data: user } = useBatchedSimpleUser(message.userId)

  const isSystemMessage = message.userId === -1 || message.userId == null

  const { t } = useTranslation("app_server")

  const { ref: inViewportRef, inViewport } = useInViewport()

  React.useEffect(() => {
    onInViewportChange && onInViewportChange(inViewport)
  }, [inViewport])

  const { data: authUser } = useGetAuthenticatedUser()

  const [hovered, setHovered] = React.useState(false)

  const clipboardAvailable = useClipboardAvailable()
  const isOwnMessage = !isSystemMessage && authUser.userId === user?.userId

  const [
    updateModalOpened,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useDisclosure()

  const canBeEdited =
    isOwnMessage &&
    !message.isDeleted &&
    dayjs().diff(dayjs(message.timeStamp), "minutes") <= 15

  const justifyProp = isSystemMessage ? "center" : "normal"

  return (
    <DefaultListItem
      align="start"
      gap={10}
      p={5}
      ref={inViewportRef}
      justify={justifyProp}
      style={
        authUser.userId === user?.userId
          ? {
              flexDirection: "row-reverse",
            }
          : undefined
      }
      {...props}
      onMouseEnter={(event) => {
        setHovered(true)
        props.onMouseEnter?.(event)
      }}
      onMouseLeave={(event) => {
        setHovered(false)
        props.onMouseLeave?.(event)
      }}
    >
      {showAvatars && !isOwnMessage && !isSystemMessage ? (
        <PersonProfilePictureAvatar
          personId={user?.personId}
          fallbackName={user?.userName}
        />
      ) : null}
      <ChatMessagePaper
        isFocused={isFocused}
        showTimestamp
        showReadIndicator={showReadIndicator}
        showReplyToMessage
        showUserName={showUserName}
        maw={isSystemMessage ? "60%" : "80%"}
        message={message}
        highlight={highlight}
        style={isSystemMessage ? { alignSelf: "center" } : undefined}
      />
      {hovered ? (
        <>
          {!isSystemMessage && !readOnly && onReplyButtonClick && hovered ? (
            <ActionIcon
              aria-label={t("Reply")}
              title={t("Reply")}
              onClick={onReplyButtonClick}
              variant="subtle"
              c="gray.6"
              style={{ pointerEvents: "all" }}
            >
              <IconMessageReply />
            </ActionIcon>
          ) : null}
          {!!message.postText && clipboardAvailable && !isSystemMessage && (
            <CopyButton value={message.postText} timeout={2000}>
              {({ copied, copy }) => (
                <ActionIcon
                  aria-label={
                    copied ? t("CopiedToClipboard") : t("CopyToClipboard")
                  }
                  title={copied ? t("CopiedToClipboard") : t("CopyToClipboard")}
                  style={{ pointerEvents: "all" }}
                  color={copied ? "teal" : "gray"}
                  variant="subtle"
                  c="gray.6"
                  onClick={copy}
                >
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
              )}
            </CopyButton>
          )}
          {canBeEdited && !isSystemMessage && (
            <ActionIconPencil
              variant="subtle"
              c="gray.6"
              aria-label={t("EditMessage")}
              onClick={openUpdateModal}
            />
          )}
        </>
      ) : null}
      <UpdateMessageModal
        opened={updateModalOpened}
        onClose={closeUpdateModal}
        postId={message.chatPostId}
        message={message.postText}
      />
    </DefaultListItem>
  )
}
